import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {CircleAlert, CircleCheck, Clock} from 'lucide-react';
import {useQuery} from '@tanstack/react-query';
import DashboardLayout from '../components/layout/DashboardLayout';
import PageHeader from '../components/layout/PageHeader';
import ProviderProducts from '../components/provider/ProviderProducts';
import PatientOrdersTable, {
    type OrderSearchFields,
    type PatientOrderRow,
} from '../components/provider/PatientOrdersTable';
import SalesReportChart from '../components/provider/SalesReportChart';
import NotificationsPanel from '../components/provider/NotificationsPanel';
import SchedulingCalendar from '../components/provider/SchedulingCalendar';
import {Recommendation} from '@/types/api';
import {useAuth} from '../hooks/useAuth';
import {ProviderConsoleService, UserService} from '../services';
import type {ProviderSalesReportResponse} from '@/services/providerConsoleService';
import type {ProviderOrder} from '@/types/api';
import '../styles/typography.css';
import '../styles/colors.css';
import {decodeJwtToken, getOrganizationIdFromTokenPayload} from '@/utils/tokenUtils';
import type {StatusTone, StatusToneStyles} from '@/components/provider/statusTone';

// Use centralized tone/style types shared by Provider UI components.
const statusToneStyles: StatusToneStyles = {
    success: {pill: 'text-[#2D8473]', action: 'text-[#2D8473]', icon: CircleCheck},
    warning: {pill: 'text-[#B5741B]', action: 'text-[#B5741B]'},
    danger: {pill: 'text-[#D35A73]', action: 'text-[#D35A73]', icon: CircleAlert},
    info: {pill: 'text-[#4C74A8]', action: 'text-[#4C74A8]'},
    default: {pill: '', action: ''},
};

const reportTabs = ['Last 7 Days', 'Month', 'Year', 'Custom Range'];

function formatEventDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

const notifications = [
    {
        id: 1,
        title: 'Kelly Paulson Results require clinical review',
        detail: 'Biotin Clinical Panel',
        cta: 'Review',
        tone: 'danger' as StatusTone,
        icon: CircleAlert,
    },
    {
        id: 2,
        title: 'Upcoming Telehealth Appointment',
        detail: 'Dr. Reeves · October 18 · 1:30 pm',
        cta: 'Details',
        tone: 'info' as StatusTone,
        icon: Clock,
    },
    {
        id: 3,
        title: 'Replace Wellness Kits',
        detail: '3 kits remaining in inventory',
        cta: 'Review',
        tone: 'warning' as StatusTone,
        icon: Clock,
    },
    {
        id: 4,
        title: 'Jessica Collins Results ready',
        detail: 'Advanced Thyroid Panel',
        cta: 'Review',
        tone: 'success' as StatusTone,
        icon: CircleAlert,
    },
    {
        id: 5,
        title: 'Upcoming Telehealth Appointment',
        detail: 'Dr. Reeves · October 21 · 11:00 am',
        cta: 'Details',
        tone: 'default' as StatusTone,
        icon: Clock,
    },
];

function buildMockScheduleEvents() {
    const today = new Date();
    const todayKey = formatEventDateKey(today);
    return [
        {
            id: 1,
            title: 'Telehealth w/ Mary Jordan',
            time: '9:00 – 10:00 am CST',
            description: 'Telehealth · Johntahan Randolph',
            date: todayKey,
        },
        {
            id: 2,
            title: 'Telehealth w/ Johntahan Randolph',
            time: '12:00 – 1:15 pm CST',
            description: 'Clinic Onboarding · Argiro Argiolas',
            date: formatEventDateKey(addDays(today, 2)),
        },
        {
            id: 3,
            title: 'Staff Training',
            time: '2:30 – 4:00 pm CST',
            description: 'In-person training · Team',
            date: formatEventDateKey(addDays(today, 5)),
        },
    ];
}

const mapStatusToTone = (status: string | null | undefined): StatusTone => {
    if (!status) {
        return 'default';
    }

    const normalized = status.toLowerCase();
    if (normalized.includes('critical') || normalized.includes('alert') || normalized.includes('error')) {
        return 'danger';
    }
    if (normalized.includes('release') || normalized.includes('complete') || normalized.includes('ready')) {
        return 'success';
    }
    if (normalized.includes('pending') || normalized.includes('processing')) {
        return 'info';
    }
    if (normalized.includes('review')) {
        return 'warning';
    }
    return 'default';
};

const formatOrderDate = (value: string | null | undefined): string => {
    if (!value) {
        return '—';
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString('en-US');
};

const formatPatientName = (firstName: string | null, lastName: string | null): string => {
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    return fullName || 'Unknown Patient';
};

const mapOrderToDisplay = (order: ProviderOrder): PatientOrderRow => ({
    id: order.id,
    patient: formatPatientName(order.firstName, order.lastName),
    panel: order.oid || 'Lab Order',
    orderDate: formatOrderDate(order.createdDate),
    createdDate: order.createdDate,
    statusLabel: order.status ?? 'Pending',
    tone: mapStatusToTone(order.status),
    actionLabel: 'View Order',
});

type RawOrderSearchResult = {
    id?: number | string | null;
    order_id?: number | string | null;
    orderID?: number | string | null;
    oid?: number | string | null;
    firstName?: string | null;
    first_name?: string | null;
    lastName?: string | null;
    last_name?: string | null;
    status?: string | null;
    createdDate?: string | number | Date | null;
    created_date?: string | number | Date | null;
    created_at?: string | number | Date | null;
    statusDate?: string | number | Date | null;
    status_date?: string | number | Date | null;
    status_updated?: string | number | Date | null;
};

// Provider-order search results come from different legacy endpoints that don't share a strict schema.
// Normalize the field variants so the Provider UI can render consistently.
function normalizeDateToIso(value: unknown): string | null {
    if (value instanceof Date) {
        return value.toISOString();
    }
    if (typeof value === 'number') {
        const date = new Date(value);
        // Don't fall back to "now" for invalid dates; it hides data issues and breaks sorting.
        return Number.isNaN(date.getTime()) ? null : date.toISOString();
    }
    if (typeof value === 'string') {
        const date = new Date(value);
        // Don't fall back to "now" for invalid dates; it hides data issues and breaks sorting.
        return Number.isNaN(date.getTime()) ? null : date.toISOString();
    }
    // Unknown date types should remain unknown instead of becoming "now".
    return null;
}

function normalizeNullableDateToIso(value: unknown): string | null {
    if (value == null) return null;
    if (value === '') return null;
    return normalizeDateToIso(value);
}

const normalizeOrderFromSearch = (order: RawOrderSearchResult): ProviderOrder | null => {
    const createdDate =
        normalizeDateToIso(order.createdDate ?? order.created_date ?? order.created_at) ?? new Date(0).toISOString();
    const statusDate = normalizeNullableDateToIso(order.statusDate ?? order.status_date ?? order.status_updated);

    const rawId = order.id ?? order.order_id ?? order.orderID;
    const numericId = Number(rawId);
    // Don't generate ids for rows that don't have a real order id.
    // Fake ids (e.g. Date.now()) cause unstable React keys and broken `/result/:id` links.
    if (!Number.isFinite(numericId) || numericId <= 0) {
        return null;
    }
    const id = numericId;

    const oid =
        typeof order.oid === 'string'
            ? order.oid
            : order.oid != null
              ? String(order.oid)
              : rawId != null
                ? String(rawId)
                : '';

    return {
        id,
        oid,
        firstName: order.firstName ?? order.first_name ?? '',
        lastName: order.lastName ?? order.last_name ?? '',
        status: order.status ?? '',
        createdDate,
        statusDate,
    };
};

const ProviderConsole: React.FC = () => {
    const showMockData = import.meta.env.DEV;
    const [activeRange, setActiveRange] = useState(reportTabs[0]);
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const {token} = useAuth();
    const [providerOrders, setProviderOrders] = useState<ProviderOrder[]>([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [ordersError, setOrdersError] = useState<string | null>(null);
    const notificationsData = showMockData ? notifications : [];
    const scheduleEventsData = useMemo(() => (showMockData ? buildMockScheduleEvents() : []), [showMockData]);

    const canFetchRecommendations = Boolean(token);
    const recommendationsScopeKey = useMemo(() => {
        // Include org/role/type/impersonation in the key so React Query doesn't leak cached results across users.
        if (!token) return 'anonymous';
        const payload = decodeJwtToken(token);
        if (!payload) return 'unknown';
        const orgId = getOrganizationIdFromTokenPayload(payload) ?? '';
        const roleId = payload.role_id ?? '';
        const typeId = payload.type_id ?? '';
        const impersonateId = payload.impersonate_id ?? '';
        return `${orgId}:${roleId}:${typeId}:${impersonateId}`;
    }, [token]);
    const recommendationsQuery = useQuery({
        queryKey: ['recommendations-catalog', recommendationsScopeKey],
        queryFn: () => UserService.getRecommendations(),
        enabled: canFetchRecommendations,
        staleTime: 5 * 60 * 1000,
    });

    const fetchProviderOrders = useCallback(async () => {
        setOrdersLoading(true);
        setOrdersError(null);
        try {
            // The API resolves organization from tenant context (req.tenant.id).
            const response = await ProviderConsoleService.getPatientOrders({limit: 15});
            setProviderOrders(response.orders);
        } catch (error) {
            const message =
                error instanceof Error ? error.message : 'An unexpected error occurred while loading orders.';
            setOrdersError(message);
        } finally {
            setOrdersLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProviderOrders();
    }, [fetchProviderOrders]);

    const providerRecommendations = React.useMemo(() => {
        const recommendations = recommendationsQuery.data ?? [];
        const recommendationsMap = new Map<number, Recommendation>();

        recommendations.forEach((rec) => {
            if (rec.enabled) {
                recommendationsMap.set(rec.Id, rec);
            }
        });

        return Array.from(recommendationsMap.values());
    }, [recommendationsQuery.data]);

    const patientOrders = React.useMemo(() => providerOrders.map(mapOrderToDisplay), [providerOrders]);

    const handleSearchOrders = useCallback(async (fields: OrderSearchFields) => {
        const data = await ProviderConsoleService.searchPatientOrders({
            ...fields,
            limit: 50,
        });

        // Filter out search rows without a valid order id so we don't render broken links.
        const rawOrders = data?.orders ?? [];
        const normalized = rawOrders
            .map((order) => normalizeOrderFromSearch(order))
            .filter((order): order is ProviderOrder => Boolean(order));
        const droppedCount = rawOrders.length - normalized.length;

        const toast =
            normalized.length === 0
                ? {error: false, message: 'No orders found.'}
                : {
                      error: false,
                      message: `${normalized.length} order(s) found.${
                          droppedCount > 0 ? ` (${droppedCount} omitted: missing order id)` : ''
                      }`,
                  };

        return {orders: normalized.map(mapOrderToDisplay), toast};
    }, []);

    const salesRangeParam: ProviderSalesReportResponse['range'] = React.useMemo(() => {
        switch (activeRange) {
            case 'Month':
                return 'month';
            case 'Year':
                return 'year';
            case 'Custom Range':
                return 'custom';
            case 'Last 7 Days':
            default:
                return 'last7';
        }
    }, [activeRange]);

    const salesQuery = useQuery({
        queryKey: ['providerSalesReport', salesRangeParam],
        queryFn: () => ProviderConsoleService.getSalesReport({range: salesRangeParam}),
    });

    const reportSeries = salesQuery.data?.series ?? [];
    const salesSeries = reportSeries.map((p) => p.salesAmount);
    const salesChartData = reportSeries.map((p) => ({label: p.label, value: p.salesAmount}));

    const formatCurrency = (value: number) => value.toLocaleString('en-US', {style: 'currency', currency: 'USD'});

    const salesStats = React.useMemo(() => {
        const data = salesQuery.data;
        if (!data) {
            return [
                {label: 'Sales This Period', value: '—'},
                {label: 'Avg Daily Sales', value: '—'},
                {label: 'Orders Placed', value: '—'},
                {label: 'Conversion Rate', value: '—'},
            ];
        }

        const conversionRate =
            typeof data.conversionRate === 'number' && Number.isFinite(data.conversionRate)
                ? `${Math.round(data.conversionRate * 100)}%`
                : '—';

        return [
            {label: 'Sales This Period', value: formatCurrency(data.salesThisPeriod)},
            {label: 'Avg Daily Sales', value: formatCurrency(data.avgDailySales)},
            {label: 'Orders Placed', value: `${data.ordersPlaced}`},
            {label: 'Conversion Rate', value: conversionRate},
        ];
    }, [salesQuery.data]);

    const maxSalesValue = salesSeries.length ? Math.max(...salesSeries) + 20 : 0;

    return (
        <DashboardLayout>
            <div className="relative">
                <div className="">
                    <PageHeader title="Provider Console" subtitle="Operational patient overview" />

                    <div className="mt-8 grid gap-8 lg:grid-cols-[2.2fr_1fr] max-w-full overflow-hidden">
                        <PatientOrdersTable
                            orders={patientOrders}
                            ordersLoading={ordersLoading}
                            ordersError={ordersError}
                            statusToneStyles={statusToneStyles}
                            onSearchOrders={handleSearchOrders}
                        />

                        <div className="space-y-8 min-w-0">
                            <SalesReportChart
                                reportTabs={reportTabs}
                                activeRange={activeRange}
                                onSelectRange={setActiveRange}
                                salesStats={salesStats}
                                salesChartData={salesChartData}
                                maxSalesValue={maxSalesValue}
                            />

                            <div>
                                <ProviderProducts
                                    recommendations={providerRecommendations}
                                    loading={
                                        !canFetchRecommendations ||
                                        recommendationsQuery.isLoading ||
                                        recommendationsQuery.isFetching
                                    }
                                />
                            </div>
                        </div>

                        <div className="space-y-8 min-w-0">
                            <NotificationsPanel notifications={notificationsData} statusToneStyles={statusToneStyles} />
                            <SchedulingCalendar
                                selectedDate={selectedDate}
                                onChangeSelectedDate={setSelectedDate}
                                events={scheduleEventsData}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default ProviderConsole;
